/**
 * Presenter mode, the "presented window" (2026-10-27). Loaded first by src/main.tsx; for everyone
 * else it only reads one sessionStorage key and returns false.
 *
 * The presenter dashboard (/admin/presenter, super-admin allowlist, see functions/src/presenter.ts)
 * opens a second window at /present#<code> after writing the same one-time code to localStorage.
 * Only when both match does this tab become a presented window, for the life of the tab (a
 * sessionStorage flag, so it survives a refresh). In that tab:
 *   - Firebase Auth keeps its session in sessionStorage (src/lib/firebase.ts), so the tab can be
 *     signed in as a demo persona while every other tab stays signed in as the presenter.
 *   - localStorage is replaced, for this tab only, by a sessionStorage-backed store, so the app's
 *     own saved keys (role, theme, last route, drafts) never leak between the two sign-ins.
 *   - Firestore runs without offline persistence (src/main.tsx), so no cache is shared either.
 * A visitor who types /present gets the normal site: without the matching code nothing changes.
 */
export const PRESENTED_FLAG = 'arevina.presented';
export const PRESENT_PAIR_KEY = 'arevina.presentPair';
export const PRESENT_PATH = '/present';

function detect(): boolean {
  try {
    if (sessionStorage.getItem(PRESENTED_FLAG) === '1') return true;
    if (window.location.pathname !== PRESENT_PATH) return false;
    const code = window.location.hash.slice(1);
    const expected = localStorage.getItem(PRESENT_PAIR_KEY);
    if (!code || !expected || code !== expected) return false;
    localStorage.removeItem(PRESENT_PAIR_KEY);
    sessionStorage.setItem(PRESENTED_FLAG, '1');
    return true;
  } catch {
    return false;
  }
}

/** A Storage for this tab only, kept in sessionStorage under "ls:" so it survives a refresh. */
function installTabLocalStorage(): void {
  const s = window.sessionStorage;
  const P = 'ls:';
  const keys = (): string[] => {
    const out: string[] = [];
    for (let i = 0; i < s.length; i++) {
      const k = s.key(i);
      if (k && k.startsWith(P)) out.push(k.slice(P.length));
    }
    return out;
  };
  const store = {
    get length() { return keys().length; },
    key: (i: number) => keys()[i] ?? null,
    getItem: (k: string) => s.getItem(P + k),
    setItem: (k: string, v: string) => s.setItem(P + k, String(v)),
    removeItem: (k: string) => s.removeItem(P + k),
    clear: () => keys().forEach((k) => s.removeItem(P + k)),
  };
  // The app only uses getItem/setItem/removeItem/key/length (no localStorage[key] access).
  Object.defineProperty(window, 'localStorage', { configurable: true, get: () => store });
}

export const isPresentedWindow: boolean = typeof window !== 'undefined' && detect();
if (isPresentedWindow) installTabLocalStorage();
